import type { ScenarioSpec } from "./types";
import { phishingOfficeScenario as PHISH } from "./phishing-office.scenario";

/**
 * Inside the Bank: One Password Away.
 *
 * The course runs inside the `bankstory` orchestrator
 * (BankStory.tsx), which REUSES the real Desktop / Mail (Outlook) / Browser
 * (Chrome) components for every standard screen and only draws custom screens
 * for the attacker's own tooling. A persistent coach states the role + reason
 * for every click and keeps the 5-step list visible the whole game.
 *
 * Reuses the phishing-office OFFICE scene (monitor framing) but never alters it.
 */
export const insideTheBankScenario: ScenarioSpec = {
  version: 1,
  id: "inside-the-bank",
  title: "Inside the Bank: One Password Away",
  summary: {
    goal: "See how a reused password pattern, a fake sign-in page and public profile facts let an attacker in, and why every account needs its own password.",
    youDo: "Read the leak article, try to get into Alex's work mailbox, send a message from it, watch who answers, collect clues from a public profile and run a password-guessing tool.",
    duration: "10-15 min",
    tags: ["passwords", "leaked_credentials", "fake_login_pages", "email_phishing", "oversharing"],
    audience: ["all_staff"],
  },
  topic: "Play the attacker. A leaked password, a fake salary page, and public clues.",
  language: "en",
  passThreshold: 60,
  allowFreeExplore: false,
  voiceAttackers: PHISH.voiceAttackers,
  scene: PHISH.scene,

  devices: [
    {
      id: "bank_pc",
      kind: "bankstory",
      config: {
        email: "alex.morgan@asterbank.example",
        leakedPassword: "BestSummer2025!",
        mailboxPassword: "BestSummer2026!",
      },
    },
  ],

  beats: [
    {
      id: "b_intro",
      type: "intro",
      title: "Inside the Bank",
      kicker: "One password away",
      icon: "eye",
      guide: {
        title: "Before you start",
        body: "You play the attacker. A streaming service's password leak is in the news, and one row belongs to Alex Morgan, an employee of the fictional Aster Bank. It starts at your computer, with only the browser open.",
      },
      ctaLabel: "Begin",
    },
    { id: "b_to_monitor", type: "camera", waypointId: "wp_monitor" },
    {
      id: "b_bank",
      type: "device",
      deviceId: "bank_pc",
      guide: {
        title: "Inside the Bank",
        body: "Everything happens on this PC. The coach on the right states your role and the reason for each click, and tracks the 5 steps.",
      },
      completeOn: { action: "continue" },
    },
    {
      id: "b_quiz",
      type: "quiz",
      title: "Check your understanding",
      quiz: [
        {
          id: "q_pattern",
          title: "Question 1",
          prompt: "Alex's streaming password was BestSummer2025!. The work mailbox opened with BestSummer2026!. What made the mailbox password easy to work out?",
          options: [
            { id: "a", label: "Nothing — changing the year makes it a brand-new secret.", icon: "x" },
            { id: "b", label: "The pattern stayed the same; only the year moved.", correct: true, icon: "check" },
            { id: "c", label: "The bank and the streaming service share one login.", icon: "alert" },
          ],
          rationale: "Editing one familiar password leaves a predictable pattern. Each account needs its own password, made from scratch.",
        },
        {
          id: "q_click",
          title: "Question 2",
          prompt: "A colleague receives the fake salary message. Which action actually handed over a password?",
          options: [
            { id: "a", label: "Opening and reading the email.", icon: "x" },
            { id: "b", label: "Clicking the link and then closing the page.", icon: "x" },
            { id: "c", label: "Typing their credentials into the fake sign-in page.", correct: true, icon: "check" },
          ],
          rationale: "Opening or clicking is not a disclosure here. The password is only lost when it's typed into the attacker's page.",
        },
        {
          id: "q_strength",
          title: "Question 3",
          prompt: "Dan used a long, random password — much harder to guess. Why was it still collected?",
          options: [
            { id: "a", label: "Strong passwords are easy to crack.", icon: "x" },
            { id: "b", label: "He typed it into the fake page, so strength didn't matter.", correct: true, icon: "check" },
            { id: "c", label: "The attacker guessed it with AI.", icon: "alert" },
          ],
          rationale: "Strength stops guessing. It does not stop phishing. An exposed password is unsafe no matter how strong.",
        },
        {
          id: "q_clues",
          title: "Question 4",
          prompt: "The guessing tool built Biscuit2004 from Maya's public profile. What did it actually do?",
          options: [
            { id: "a", label: "It uncovered a secret it had never been shown.", icon: "x" },
            { id: "b", label: "It mixed public facts into a familiar pattern.", correct: true, icon: "check" },
            { id: "c", label: "It broke a long random passphrase the same way.", icon: "alert" },
          ],
          rationale: "A pet name and a birth year are public building blocks. A password made of random unrelated words gives that tool nothing to assemble.",
        },
        {
          id: "q_ai",
          title: "Question 5",
          prompt: "The guessing tool tried several passwords on the bank. Which one was accepted?",
          options: [
            { id: "a", label: "A secret the tool invented with no connection to the leak.", icon: "x" },
            { id: "b", label: "BestSummer2026! — the leaked password with this year.", correct: true, icon: "check" },
            { id: "c", label: "Every guess, because the bank has no password check.", icon: "alert" },
          ],
          rationale: "The tool tried the leak, a wrong year, and facts from the public profile. The bank accepted the same words with this year.",
        },
      ],
    },
  ],

  edges: [
    { from: "b_intro", to: "b_to_monitor" },
    { from: "b_to_monitor", to: "b_bank" },
    { from: "b_bank", to: "b_quiz" },
  ],

  initialBeatId: "b_intro",
};
